import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import moment from "moment";
import exportFromJSON from "export-from-json";
import { withAppHOC } from "../../../hoc";
import PageHeading from "../../../components/PageHeader/PageHeader";
import LoadingSpinner from "../../../components/LoadingSpinner/LoadingSpinner";
import NoData from "../../../components/NoData/NoData";
import {
  MessageBox,
  Form,
  Fieldset,
  TextInput,
  ResetButton,
  Button,
  Label,
  Dropdown,
} from "../../../components/FormElements";
import { TableBuilder } from "../../../components/TableElements";
import Pagination from "../../../components/Pagination/Pagination";
import { StyledFiltersContainer, StyledSpan } from "../../../components/Styled";
import API from "../../../api";
import { API_RESPONSE_TYPES } from "../../../constants";
import {
  getSearchParams,
  formatCurrency,
  shouldRedirect,
  redirectToPageOne,
  camelCaseToString,
} from "../../../utils";
import FiltersButton from "../../../components/FiltersButton/FiltersButton";

const UsersFundSwitchingReport = (props) => {
  const [paginationData, setPaginationData] = useState({
    pageNumber: getSearchParams("pageNumber") || 1,
    recordsPerPage: getSearchParams("recordsPerPage") || 10,
  });
  const id = 0;
  const [totalRecords, setTotalRecords] = useState(0);
  const [isFiltering, setIsFiltering] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [responseStatus, setResponseStatus] = useState("");
  const [responseMessage, setResponseMessage] = useState("");
  const [fundSwitchingTransactions, setFundSwitchingTransactions] = useState(
    []
  );
  const [users, setUsers] = useState([]);
  const [fUsersId, setFusersId] = useState("");
  const [type, setType] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const resetForm = () => {
    setFusersId("");
    setType("");
    setFromDate("");
    setToDate("");
    readData("", "", "", "");
  };

  const readUsers = useCallback(() => {
    setIsLoading(true);
    setResponseStatus("");
    setResponseMessage("");
    API.get("/accounts-management/users?pageNumber=1&recordsPerPage=100000")
      .then((response) => {
        const { status, message, data } = response.data;
        if (status === API_RESPONSE_TYPES.FAILED) {
          setResponseStatus(status);
          setResponseMessage(message);
        } else {
          setUsers(data);
        }
      })
      .catch((error) => {
        setResponseStatus(API_RESPONSE_TYPES.FAILED);
        setResponseMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const readData = useCallback(
    (fUsersId = "", type = "", fromDate = "", toDate = "") => {
      setIsLoading(true);
      setResponseStatus("");
      setResponseMessage("");
      API.get(
        `/accounts-management/users/fund-switching-transactions/${id}?pageNumber=${paginationData.pageNumber}&recordsPerPage=${paginationData.recordsPerPage}&type=${type}&fromDate=${fromDate}&toDate=${toDate}&fUsersId=${fUsersId}`
      )
        .then((response) => {
          const { status, message, data, pageInfo } = response.data;
          if (shouldRedirect(data?.length, paginationData?.pageNumber)) {
            redirectToPageOne();
          } else {
            if (status === API_RESPONSE_TYPES.FAILED) {
              setResponseStatus(status);
              setResponseMessage(message);
            } else {
              setFundSwitchingTransactions(data);
              setTotalRecords(pageInfo.totalRecords);
            }
          }
        })
        .catch((error) => {
          setResponseStatus(API_RESPONSE_TYPES.FAILED);
          setResponseMessage(error.message);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [paginationData, id]
  );

  const onClickExport = () => {
    exportFromJSON({
      data: fundSwitchingTransactions,
      fileName: "fund_switching_transactions",
      exportType: exportFromJSON.types.csv,
    });
  };

  const reloadData = () => {
    readData(fUsersId, type, fromDate, toDate);
  };

  const filterData = (e) => {
    e.preventDefault();
    setIsFiltering(true);
  };

  useEffect(() => {
    if (isFiltering) {
      setPaginationData((paginationData) => ({
        pageNumber: 1,
        recordsPerPage: paginationData.recordsPerPage,
      }));
      setIsFiltering((isFiltering) => !isFiltering);
    }
  }, [isFiltering]);

  useEffect(() => {
    if (!isFiltering) {
      readData(fUsersId, type, fromDate, toDate);
    }
  }, [paginationData, readData]);

  useEffect(() => {
    readUsers();
  }, [readUsers]);

  const [showFilters, setShowFilters] = useState(false);
  const onClickShowFilters = () => {
    setShowFilters((showFilters) => !showFilters);
  };

  const usersOptions = [{ title: "-- SELECT USER --", value: "" }];
  users.forEach(({ id, fullName, mobileNumber }) =>
    usersOptions.push({
      title: fullName,
      value: id?.toString(),
      subTitle: mobileNumber?.toString(),
    })
  );

  return (
    <>
      <PageHeading
        {...props}
        canReloadProp={true}
        reloadData={reloadData}
        showExport={fundSwitchingTransactions.length > 0}
        onClickExport={onClickExport}
      />
      <FiltersButton onClick={onClickShowFilters} value={showFilters} />
      <StyledFiltersContainer className={showFilters ? "show" : ""}>
        <Form method="POST" action="#" onSubmit={filterData}>
          <Fieldset>
            <Label>User</Label>
            <Dropdown
              value={fUsersId}
              onChange={setFusersId}
              placeholder="User"
              disabled={isLoading}
              options={usersOptions}
              searchable
            />
          </Fieldset>
          <Fieldset>
            <Label>Switch Type</Label>
            <Dropdown
              value={type}
              onChange={setType}
              placeholder="Switch Type"
              disabled={isLoading}
              options={[
                {
                  title: "-- SELECT SWITCH TYPE --",
                  value: "",
                },
                {
                  title: "Auto",
                  value: "Auto",
                },
                {
                  title: "Admin",
                  value: "Admin",
                },
                {
                  title: "Manual",
                  value: "Manual",
                },
              ]}
            />
          </Fieldset>
          <Fieldset>
            <Label>Start Date</Label>
            <TextInput
              type="date"
              value={fromDate}
              onChange={setFromDate}
              placeholder="Start Date"
              disabled={isLoading}
            />
          </Fieldset>
          <Fieldset>
            <Label>End Date</Label>
            <TextInput
              type="date"
              value={toDate}
              onChange={setToDate}
              placeholder="End Date"
              disabled={isLoading}
            />
          </Fieldset>
          <Fieldset>
            <Button disabled={isLoading} type="submit">
              Filter
            </Button>
            <ResetButton disabled={isLoading} type="button" onClick={resetForm}>
              Reset
            </ResetButton>
          </Fieldset>
        </Form>
      </StyledFiltersContainer>
      <LoadingSpinner
        isLoading={
          responseStatus === "" &&
          fundSwitchingTransactions?.length === 0 &&
          isLoading
        }
      />
      <NoData
        status={
          responseStatus !== API_RESPONSE_TYPES.FAILED &&
          !isLoading &&
          fundSwitchingTransactions?.length === 0
        }
        message={`No transactions found`}
      />
      {responseStatus === API_RESPONSE_TYPES.FAILED && (
        <MessageBox status={responseStatus} message={responseMessage} />
      )}
      {fundSwitchingTransactions?.length > 0 && (
        <>
          <TableBuilder
            isLoading={fundSwitchingTransactions?.length !== 0 && isLoading}
            tableHeadings={[
              {
                title: "User Details",
                dataSelector: "fullName",
                dataType: "string",
                CellRenderer: (value, item) => (
                  <>
                  {value}
                  <StyledSpan>{item.mobileNumber}</StyledSpan>
                  </>
                ),
              },
              {
                title: "Date & Time",
                dataSelector: "createdOn",
                dataType: "string",
              },
              {
                title: "Amount",
                dataSelector: "amount",
                dataType: "number",
                CellRenderer: (value) => formatCurrency(value),
              },
              {
                title: "Switch Type",
                dataSelector: "type",
                dataType: "string",
              },
            ]}
            tableData={fundSwitchingTransactions}
          />
          <Pagination
            totalRecords={totalRecords}
            paginationData={paginationData}
            setPaginationData={setPaginationData}
          />
        </>
      )}
    </>
  );
};

export default withAppHOC(UsersFundSwitchingReport);
